import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";

import useAdIsAboutToExpire from "components/Homepage/hooks/useAdIsAboutToExpire";
import { useHomepageNewThisWeekProducts } from "components/Homepage/hooks/useHomepageNewThisWeekProducts.hooks";
import { SEO as SEOComponent } from "components/common";

import { HomepageHeroBlock } from "@/components/Homepage/HomepageHeroBlock";
import { HomepageIntroBlock } from "@/components/Homepage/HomepageIntroBlock";
import { LazyHydrate } from "@/common/components/lazy-hydrate";
import { HomepageAppDownloadBlock } from "@/components/Homepage/HomepageAppDownloadBlock";
import { HomepageCollectionsBlock } from "@/components/Homepage/HomepageCollectionsBlock";
import { HomepageFavoritesBlock } from "@/components/Homepage/HomepageFavoritesBlock";
import { HomepageForYouBlock } from "@/components/Homepage/HomepageForYouBlock";
import { HomepageLinkCloudBlock } from "@/components/Homepage/HomepageLinkCloudBlock";
import { HomepageMarketingBlock } from "@/components/Homepage/HomepageMarketingBlock";
import { HomepagePopularBrandsBlock } from "@/components/Homepage/HomepagePopularBrandsBlock";
import { OpenHomePromoBlock } from "@/features/open-home/components/OpenHomePromoBlock";
import { HomepagePromotions } from "@/features/promotions/components/HomepagePromotions";
import { NewThisWeek } from "@/features/search/components/NewThisWeek";
import type { ProductCardProduct } from "@/features/search/components/product-card/product-card.context";
import useShowMissingPaymentInfoModal from "@/hooks/useShowMissingPaymentInfoModal";

/*
  Same reasoning as the top banner in the header: the embedded-message SDK
  is client-only and renders nothing server-side, so keep its chunk off the
  document's eager script set.
*/
const HomepageBottomEmbeddedMessages = dynamic(
  () =>
    import(
      "@/features/iterable/components/HomepageBottomEmbeddedMessages"
    ).then(module => module.HomepageBottomEmbeddedMessages),
  { ssr: false }
);

export const Homepage = ({
  newThisWeek = [],
}: {
  newThisWeek?: ProductCardProduct[];
}) => {
  const { t } = useTranslation();
  const newThisWeekProducts = useHomepageNewThisWeekProducts(newThisWeek);

  useAdIsAboutToExpire();
  useShowMissingPaymentInfoModal();

  return (
    <>
      <SEOComponent
        title={t("homepage:homepage-seo-title")}
        description={t("homepage:homepage-seo-description")}
      />
      <HomepageIntroBlock />
      <HomepageHeroBlock />
      <div className="mx-4 my-10 flex max-w-screen-xl flex-col space-y-10 xl:container md:mx-auto md:my-20 md:space-y-20 md:px-4 xl:mx-auto">
        {/*
         * Eager: the hero is the LCP element, and Favorites renders nothing
         * for every logged-out visitor. For You and Marketing sit near the
         * fold, so LazyHydrate defers them only on screens where they start
         * out of reach; Marketing can render nothing, hence `empty:hidden`.
         */}
        {/* Open Home promotion, first thing under the hero (design 2026-10-03). */}
        <OpenHomePromoBlock />
        <LazyHydrate>
          <HomepageForYouBlock />
        </LazyHydrate>
        <HomepagePromotions />
        <LazyHydrate className="empty:hidden">
          <HomepageMarketingBlock />
        </LazyHydrate>
        <NewThisWeek products={newThisWeekProducts} variant="homepage" />
        <LazyHydrate>
          <HomepagePopularBrandsBlock />
        </LazyHydrate>
        <LazyHydrate>
          <HomepageCollectionsBlock />
        </LazyHydrate>
        <HomepageFavoritesBlock />
        <LazyHydrate>
          <HomepageAppDownloadBlock />
        </LazyHydrate>
        <LazyHydrate>
          <HomepageLinkCloudBlock />
        </LazyHydrate>
        <HomepageBottomEmbeddedMessages />
      </div>
    </>
  );
};
